import Shell from "@/components/app/Shell";
import { Link } from "react-router-dom";
import { useOverride } from "@/context/ContentContext";
import { Users, Mic, CalendarClock, ExternalLink, HeartHandshake, BookOpen, ArrowRight } from "lucide-react";

function SupportLink({ href, icon: Icon, title, blurb, testid, placeholder }) {
  const disabled = !href;
  const cls = `group flex items-start gap-4 p-6 rounded-2xl border-2 transition-all ${
    disabled
      ? "border-edge bg-cream opacity-60 cursor-not-allowed"
      : "border-edge bg-warm-white hover:border-gold hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(44,45,91,0.08)]"
  }`;
  const inner = (
    <>
      <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-navy text-gold flex items-center justify-center">
        <Icon size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-display font-bold text-navy text-lg">{title}</div>
          {!disabled && <ExternalLink size={14} className="text-ink-soft group-hover:text-navy" />}
        </div>
        <p className="mt-1 text-sm text-ink-mid leading-relaxed">{blurb}</p>
        {disabled && <div className="mt-2 text-xs italic text-mauve">{placeholder}</div>}
      </div>
    </>
  );
  if (disabled) {
    return <div className={cls} data-testid={`${testid}-disabled`}>{inner}</div>;
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>
      {inner}
    </a>
  );
}

// Fourth support tile — Myo Parent Playbook (internal route, not an external link, so we
// render as a <Link> rather than an <a target="_blank">). Same content lives at /learn and
// on the "Fell off track?" screen so parents can find it from wherever they are.
function PlaybookSupportLink() {
  return (
    <Link
      to="/learn/parent-playbook"
      data-testid="support-link-parent-playbook"
      className="group flex items-start gap-4 p-6 rounded-2xl border-2 border-edge bg-warm-white hover:border-mauve hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(44,45,91,0.08)] transition-all"
    >
      <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-mauve text-warm-white flex items-center justify-center">
        <BookOpen size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-display font-bold text-navy text-lg">Myo Parent Playbook</div>
          <ArrowRight size={14} className="text-ink-soft group-hover:text-mauve" />
        </div>
        <p className="mt-1 text-sm text-ink-mid leading-relaxed">
          Fun ideas, games, and strategies for getting your child excited and cooperative — plus a companion video, "Create a Compelling Story."
        </p>
      </div>
    </Link>
  );
}

export default function Support() {
  const community = useOverride("support", "community", "link_url", "");
  const powerHour = useOverride("support", "power_hour", "link_url", "");
  const oneOnOne = useOverride("support", "one_on_one", "link_url", "");

  return (
    <Shell>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-mauve font-semibold flex items-center gap-2">
            <HeartHandshake size={12} /> We're here with you
          </div>
          <h1 className="font-display text-4xl font-bold text-navy mt-1">Need support?</h1>
          <p className="text-ink-mid mt-2 leading-relaxed">
            Four ways to get more help — pick whatever fits where you are right now.
          </p>
        </div>

        <div className="space-y-4" data-testid="support-links">
          <SupportLink
            href={community}
            icon={Users}
            title="Join the Community"
            blurb="A private space with other Breathe·Thrive families. Ask questions, share wins, keep momentum. If you don't have access yet, Kajabi will walk you through it."
            testid="support-link-community"
            placeholder="Link coming soon — check back after your admin adds it."
          />
          <SupportLink
            href={powerHour}
            icon={Mic}
            title="Join our next Parent Power Hour"
            blurb="A live monthly Q&A with Chelcie. Bring your questions or just listen in — recordings sent after."
            testid="support-link-power-hour"
            placeholder="Registration link coming soon."
          />
          <SupportLink
            href={oneOnOne}
            icon={CalendarClock}
            title="Schedule a discounted 1:1 with Chelcie"
            blurb="Book a private session for the moments that need a human. Program-family pricing."
            testid="support-link-one-on-one"
            placeholder="Booking link coming soon."
          />
          <PlaybookSupportLink />
        </div>
      </div>
    </Shell>
  );
}
